import { useTranslation } from 'react-i18next';
import { Target, Zap, Users, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CTABanner from '../sections/CTABanner';

const VALUES = [
  { key: 'creativity', icon: Target, color: '#7D468C' },
  { key: 'innovation', icon: Zap, color: '#4EBFBF' },
  { key: 'collaboration', icon: Users, color: '#F2785C' },
  { key: 'excellence', icon: Star, color: '#7D468C' },
];

const About = () => {
  const { t } = useTranslation();

  return (
    <>
      <Navbar />
      <div data-testid="about-page" className="pt-16">
        {/* Hero/Bio section */}
        <div className="py-24 bg-[#233559] dark:bg-[#0F1C2E] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#7D468C]/12 pointer-events-none" style={{ transform: 'rotate(45deg)', transformOrigin: 'top right' }} />
          <div className="absolute bottom-0 left-0 w-48 h-48 border-2 border-[#4EBFBF]/10 pointer-events-none" style={{ transform: 'rotate(45deg)', transformOrigin: 'bottom left' }} />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Bio */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 bg-[#4EBFBF]" style={{ transform: 'rotate(45deg)' }} />
                  <span className="text-sm font-semibold text-[#4EBFBF] tracking-widest uppercase font-poppins">
                    {t('about.subtitle')}
                  </span>
                </div>
                <h1 className="font-poppins font-black text-5xl text-white mb-6">{t('about.title')}</h1>
                <p className="text-white/65 text-lg leading-relaxed font-source-sans">{t('about.bio')}</p>

                {/* Quick stats */}
                <div className="flex flex-wrap gap-8 mt-10 pt-8 border-t border-white/10">
                  {[['150+', t('hero.stat_projects')], ['8+', t('hero.stat_years')], ['50+', t('hero.stat_clients')]].map(([n, l]) => (
                    <div key={l}>
                      <div className="font-poppins font-black text-2xl text-[#7D468C]">{n}</div>
                      <div className="text-white/45 text-xs font-source-sans mt-0.5">{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Diamond-masked photo */}
              <div className="relative flex justify-center items-center">
                <div className="relative w-72 h-72">
                  {/* Outer diamond border */}
                  <div
                    className="absolute inset-0 bg-[#7D468C]/25"
                    style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}
                  />
                  {/* Inner photo */}
                  <div
                    className="absolute inset-4 overflow-hidden"
                    style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}
                  >
                    <img
                      src="https://images.pexels.com/photos/8117478/pexels-photo-8117478.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                      alt="Mikel Martinez"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                {/* Floating decorations */}
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-[#4EBFBF]/20" style={{ transform: 'rotate(45deg)' }} />
                <div className="absolute -top-4 -left-4 w-16 h-16 border-2 border-[#F2785C]/35" style={{ transform: 'rotate(45deg)' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Mission */}
        <div className="py-20 bg-white dark:bg-[#1A2639]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-3 h-3 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
              <span className="text-sm font-semibold text-[#F2785C] tracking-widest uppercase font-poppins">{t('about.mission_title')}</span>
              <div className="w-3 h-3 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
            </div>
            <p className="text-xl text-[#233559]/70 dark:text-white/60 max-w-3xl mx-auto font-source-sans leading-relaxed">
              {t('about.mission_text')}
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="py-24 bg-[#F5F5F5] dark:bg-[#0F1C2E]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-poppins font-bold text-4xl text-[#233559] dark:text-white mb-12 text-center">
              {t('about.values_title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {VALUES.map(v => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.key}
                    data-testid={`value-card-${v.key}`}
                    className="bg-white dark:bg-[#1A2639] p-8 text-center border border-transparent hover:border-[#7D468C]/30 transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300"
                  >
                    <div
                      className="w-16 h-16 mx-auto mb-5 flex items-center justify-center"
                      style={{ backgroundColor: v.color + '15', transform: 'rotate(45deg)' }}
                    >
                      <Icon size={22} style={{ color: v.color, transform: 'rotate(-45deg)' }} />
                    </div>
                    <h3 className="font-poppins font-bold text-xl text-[#233559] dark:text-white mb-2">
                      {t(`about.values.${v.key}.title`)}
                    </h3>
                    <p className="text-sm text-[#233559]/55 dark:text-white/50 font-source-sans leading-relaxed">
                      {t(`about.values.${v.key}.description`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <CTABanner />
      </div>
      <Footer />
    </>
  );
};

export default About;
